import React, { createContext, useContext, useState, useCallback } from 'react';

interface LoadMoreContextType {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  setHasNextPage: (value: boolean) => void;
  setIsFetchingNextPage: (value: boolean) => void;
  fetchNextPage: () => void;
  setFetchNextPageFunction: (fn: () => void) => void;
}

const LoadMoreContext = createContext<LoadMoreContextType | undefined>(undefined);

export const useLoadMore = () => {
  const context = useContext(LoadMoreContext);
  if (!context) {
    throw new Error('useLoadMore must be used within a LoadMoreProvider');
  }
  return context;
};

interface LoadMoreProviderProps {
  children: React.ReactNode;
  fetchNextPageFunction?: () => void;
}

export const LoadMoreProvider: React.FC<LoadMoreProviderProps> = ({ 
  children, 
  fetchNextPageFunction 
}) => {
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const [fetchNextPageFn, setFetchNextPageFn] = useState<(() => void) | null>(null);

  const fetchNextPage = useCallback(() => {
    if (fetchNextPageFn) {
      setIsFetchingNextPage(true);
      fetchNextPageFn();
    }
  }, [fetchNextPageFn]);

  const setFetchNextPageFunction = useCallback((fn: () => void) => {
    setFetchNextPageFn(() => fn);
  }, []);

  const setHasNextPageCallback = useCallback((value: boolean) => {
    setHasNextPage(value);
  }, []);

  const setIsFetchingNextPageCallback = useCallback((value: boolean) => {
    setIsFetchingNextPage(value);
  }, []);

  const value = {
    hasNextPage,
    isFetchingNextPage,
    setHasNextPage: setHasNextPageCallback,
    setIsFetchingNextPage: setIsFetchingNextPageCallback,
    fetchNextPage,
    setFetchNextPageFunction,
  };

  return (
    <LoadMoreContext.Provider value={value}>
      {children}
    </LoadMoreContext.Provider>
  );
}; 